
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { getAllPosts, getAllLessons } from "@/lib/contentQueries";
import Head from "next/head";
import Link from "next/link";
import OpeningDirectory from "../../components/blog/OpeningDirectory";
import { FaChessKnight } from "react-icons/fa";
import PageShell from "../../components/layout/PageShell";
import PageHeader from "../../components/layout/PageHeader";
import { getContentTemplate, getPostHref } from "../../lib/contentRouting";
import { client } from "../../../tina/__generated__/client";
import StaticRedirect from "@/components/navigation/StaticRedirect";
export default function Openings({ namedOpenings, guides, redirectTo }) {
    if (redirectTo) {
        return <StaticRedirect destination={redirectTo}/>;
    }
    return (<>
      <Head>
        <title>Chess Openings – Moves, Ideas and Lessons | Chess Hotel</title>
        <meta name="description" content="Explore popular chess openings, understand their key moves and ideas, and practise them with interactive lessons."/>
        <meta property="og:title" content="Chess Openings – Moves, Ideas and Lessons"/>
        <meta property="og:description" content="Explore popular chess openings, understand their key moves and ideas, and practise them with interactive lessons."/>
      </Head>
      <PageShell>
        <PageHeader title="Chess Openings" description="Explore popular openings and learn the ideas behind them." icon={FaChessKnight} action={<Link href="/learn/openings" locale={false} className="shrink-0 text-sm font-bold text-altblue-600 hover:text-altblue-800">
              Interactive lessons →
            </Link>}/>

        <div>
          {namedOpenings.length > 0 ? (<OpeningDirectory openings={namedOpenings}/>) : (<div className="rounded-lg border border-athens-gray-150 bg-white px-5 py-8 text-sm text-slate-600">
              Named opening references are coming soon.
            </div>)}
        </div>

        {guides.length > 0 && (<section className="mt-10 border-t border-athens-gray-200 pt-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                  Opening guides
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Practical advice for studying and playing openings.
                </p>
              </div>
              <Link href="/guides" locale={false} className="shrink-0 text-sm font-bold text-altblue-600 hover:text-altblue-800">
                All guides →
              </Link>
            </div>
            <div className="mt-6 divide-y divide-athens-gray-200 border-y border-athens-gray-200">
              {guides.slice(0, 3).map(({ node: post }) => (<Link key={post._sys.filename} href={getPostHref(post)} locale={false} className="group flex items-center justify-between gap-6 py-4">
                  <h3 className="font-bold leading-snug text-slate-900 group-hover:text-altblue-600">
                    {post.title}
                  </h3>
                  <span className="shrink-0 text-sm font-semibold text-slate-500">
                    Read →
                  </span>
                </Link>))}
            </div>
          </section>)}
      </PageShell>
    </>);
}
const getStaticProps = async ({ locale }) => {
    if (locale && locale !== "en") {
        return {
            props: { redirectTo: "/openings" },
        };
    }
    const [postsResponse, lessonResponse] = await Promise.all([
        getAllPosts({
            filter: {
                locale: { eq: "en" },
                category: { category: { slug: { eq: "openings" } } },
            },
        }),
        getAllLessons(),
    ]);
    const posts = postsResponse.data.postConnection.edges;
    const referencePosts = posts
        .map((edge) => edge.node)
        .filter((post) => getContentTemplate(post) === "opening-reference");
    const coveredLessonSlugs = new Set(referencePosts.flatMap((post) => [post.opening?.lessonSlug, post._sys.filename].filter(Boolean)));
    const compactReferencePosts = referencePosts.map((post) => ({
        _sys: {
            filename: post._sys.filename,
            breadcrumbs: post._sys.breadcrumbs,
        },
        title: post.title,
        template: post.template,
        category: post.category,
        opening: post.opening,
        heroBoard: post.heroBoard,
    }));
    const { Chess } = await import("@chesshotel/chess");
    const getMovePreview = (pgn?: string | null) => {
        if (!pgn)
            return "";
        try {
            const chess = new Chess();
            chess.loadPgn(pgn);
            return chess
                .history({ verbose: true })
                .slice(0, 4)
                .map((move, index) => index % 2 === 0
                ? `${Math.floor(index / 2) + 1}.${move.san}`
                : move.san)
                .join(" ");
        }
        catch {
            return "";
        }
    };
    const lessonOnlyOpenings = lessonResponse.data.lessonConnection.edges
        .map((edge) => edge.node)
        .filter((lesson) => !coveredLessonSlugs.has(lesson.slug))
        .map((lesson) => ({
        _sys: {
            filename: lesson.slug,
            breadcrumbs: ["openings", lesson.slug],
        },
        title: lesson.lesson,
        template: "opening-reference",
        category: { slug: "openings", category: "Openings" },
        opening: {
            name: lesson.lesson,
            moves: getMovePreview(lesson.lines?.[0]?.moves),
            lessonSlug: lesson.slug,
            side: lesson.playingWhite === false ? "black" : "white",
        },
        heroBoard: {
            fen: lesson.openingFen,
            playingWhite: lesson.playingWhite ?? true,
        },
    }));
    const namedOpenings = [...compactReferencePosts, ...lessonOnlyOpenings].sort((left, right) => (left.opening?.name || left.title).localeCompare(right.opening?.name || right.title));
    const guides = posts
        .filter((edge) => getContentTemplate(edge.node) !== "opening-reference")
        .map(({ node }) => ({
        node: {
            _sys: {
                filename: node._sys.filename,
                breadcrumbs: node._sys.breadcrumbs,
            },
            title: node.title,
            template: node.template,
            category: node.category,
        },
    }));
    return {
        props: {
            namedOpenings,
            guides,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a0f8bb3e92__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/openings/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0f8bb3e92__ as getStaticProps }
  